import { IntlShape } from 'react-intl';
import { BankAdditionalDetailsType, CreditRating } from 'types/bank';

const renderCreditRating = (creditRatings: CreditRating[]) =>
  creditRatings
    ?.filter?.((item) => item?.rating && item?.entity)
    .map((item) => `${item.entity}: ${item.rating}`)
    .join(', ');

export const formatBankAdditionalDetails = (
  additionalDetails: BankAdditionalDetailsType,
  intl: IntlShape,
  t,
  messages: Record<string, string>,
) => {
  try {
    const formattedData = {
      footnote: t(messages?.footnote, {
        date: intl.formatDate(additionalDetails?.updated_at),
      }),
      tableFields: [
        {
          id: 'founding_year',
          title: t(messages?.foundingYear),
          value: additionalDetails?.founding_year,
        },
        {
          id: 'founding_place',
          title: t(messages?.foundingPlace),
          value: additionalDetails?.founding_location,
        },
        {
          id: 'employee_count',
          title: t(messages?.employeeCount),
          value: additionalDetails?.employee_count,
        },
        {
          id: 'branch_count',
          title: t(messages?.branchCount),
          value: additionalDetails?.branch_count,
        },
        {
          id: 'major_services',
          title: t(messages?.majorServices),
          value: additionalDetails?.major_services,
        },
        {
          id: 'distribution_focus',
          title: t(messages?.distributionFocus),
          value: additionalDetails?.business_focus,
        },
        {
          id: 'branch_markets',
          title: t(messages?.branchMarkets),
          value: additionalDetails?.markets,
        },
        {
          id: 'total_capital_ratio',
          title: t(messages?.totalCapitalRatio),
          value:
            additionalDetails?.total_capital_ratio &&
            intl.formatNumber(additionalDetails?.total_capital_ratio / 100, {
              style: 'percent',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }),
        },
        {
          id: 'min_required_total_capital_ratio',
          title: t(messages?.minRequiredTotalCapitalRatio),
          value:
            additionalDetails?.minimum_required_total_capital_ratio &&
            intl.formatNumber(additionalDetails?.minimum_required_total_capital_ratio / 100, {
              style: 'percent',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }),
        },
        {
          id: 'most_recent_roe',
          title: t(messages?.mostRecentROE),
          value:
            additionalDetails?.most_recent_roe &&
            intl.formatNumber(additionalDetails?.most_recent_roe / 100, {
              style: 'percent',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }),
        },
        {
          id: 'avg_3yr_roe',
          title: t(messages?.avg3YearROE),
          value:
            additionalDetails?.average_roe &&
            intl.formatNumber(additionalDetails?.average_roe / 100, {
              style: 'percent',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }),
        },
        {
          id: 'listed',
          title: t(messages?.listed),
          value: additionalDetails?.listed ? t(messages?.isListed) : t(messages?.isNotListed),
        },
        {
          id: 'market_cap',
          title: t(messages.marketCap),
          value:
            additionalDetails?.listed &&
            additionalDetails?.market_cap &&
            additionalDetails?.currency
              ? t(messages?.marketCapInMillions, {
                  marketCap: additionalDetails?.market_cap,
                  currency: additionalDetails?.currency,
                })
              : '',
        },
        {
          id: 'bank_rating',
          title: t(messages?.bankRating),
          value: renderCreditRating(additionalDetails?.bank_rating),
        },
        {
          id: 'parent_rating',
          title: t(messages?.parentRating),
          value: renderCreditRating(additionalDetails?.parent_rating),
        },
        {
          id: 'parent_company_shares',
          title: t(messages?.parentCompanyShares),
          value:
            additionalDetails?.parent_shares &&
            intl.formatNumber(additionalDetails?.parent_shares / 100, {
              style: 'percent',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }),
        },
        {
          id: 'dgs_country',
          title: t(messages?.dgsCountry),
          value: additionalDetails?.deposit_guarantee_scheme?.country,
        },
        {
          id: 'dgs_amount',
          title: t(messages?.dgsAmount),
          value: intl.formatNumber(additionalDetails?.deposit_guarantee_scheme?.amount, {
            style: 'currency',
            currency: additionalDetails?.deposit_guarantee_scheme?.currency,
          }),
        },
        {
          id: 'country_rating',
          title: t(messages?.countryRating),
          value: renderCreditRating(additionalDetails?.country_rating),
        },
      ],
    };

    return {
      ...formattedData,
      tableFields: formattedData?.tableFields?.filter((row) => !!row?.value),
    };
  } catch {
    return null;
  }
};

export type FormattedBankAdditionalDetails = ReturnType<typeof formatBankAdditionalDetails>;
